import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from '../../components/ui/Spinner/Spinner';
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import { getParticipantByPetIncubationId, sendParticipantSurvey } from '../../controllers/participants';
import { mergeDuplicatePet } from '../../controllers/pets';
import { submitExternalConsultation, getExternalChatMessages } from '../../controllers/external';
import { displayStringToKey } from '../../utils/StringUtils';
import { useToast } from '../../contexts/ToastContext';
import './ParticipantDetailsScreen.css';

const PET_FIELDS = [
  { label: 'Type',                       keys: [displayStringToKey('Pet Type')] },
  { label: 'Name',                       keys: [displayStringToKey('Pet First Name')] },
  { label: 'Birthday',                   keys: [displayStringToKey('Pet Birthdate - Reported')], format: 'date' },
  { label: 'Weight',                     keys: [displayStringToKey('Pet Weight (lbs)')], suffix: ' lbs' },
  { label: 'Gender',                     keys: [displayStringToKey('Pet Sex')] },
  { label: 'Spayed / Neutered / Intact', keys: [displayStringToKey('Pet Spayed / Neutered / Intact')] },
  { label: 'Breed',                      keys: [displayStringToKey('Pet Breed Reported - Primary'), displayStringToKey('Pet Breed Reported - Secondary')], join: ' / ' },
  { label: 'Body Condition Score',       keys: [displayStringToKey('Body Condition Score')] },
];

const PARENT_FIELDS = [
  {
    label: 'Name',
    keys: [displayStringToKey('Owner First Name'), displayStringToKey('Owner Last Name')],
    join: ' ',
  },
  { label: 'Email Address', keys: [displayStringToKey('Owner Email Address')] },
  { label: 'Phone Number',  keys: [displayStringToKey('Owner Phone Number')], format: 'phone' },
];

const formatPhoneNumber = (raw) => {
  const str = String(raw);
  const digits = str.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return str;
};

const formatValue = (raw, format) => {
  if (raw === null || raw === undefined || raw === '') return '';
  if (format === 'date') {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return String(raw);
  }
  if (format === 'phone') return formatPhoneNumber(raw);
  if (raw === true) return 'Yes';
  if (raw === false) return 'No';
  return String(raw);
};

const readFields = (fields, spec) => {
  const parts = spec.keys
    .map(k => {
      const v = fields?.[k];
      return v === null || v === undefined ? '' : String(v).trim();
    })
    .filter(Boolean);
  if (parts.length === 0) return '';
  const joined = spec.join ? parts.join(spec.join) : parts[0];
  const formatted = formatValue(joined, spec.format);
  return spec.suffix && formatted ? `${formatted}${spec.suffix}` : formatted;
};

const formatEngagementText = (text) => {
  if (!text || typeof text !== 'string') return 'N/A';
  return text
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const formatConsultationType = (type) => {
  if (!type || typeof type !== 'string') return 'N/A';
  const upper = type.toUpperCase();
  if (upper === 'CHAT' || upper === 'THREAD') return 'Chat';
  if (upper === 'VIDEO') return 'Video';
  return formatEngagementText(type);
};

const extractMessagesFromResponse = (raw) => {
  if (!raw) return [];
  const candidates = Array.isArray(raw)
    ? raw
    : raw.messages || raw.chat_messages || raw.data?.messages || raw.data?.chat_messages || [];
  if (!Array.isArray(candidates)) return [];
  return candidates.map((m, idx) => {
    const typeUpper = String(m?.type || m?.message_type || m?.kind || '').toUpperCase();
    const isImage = typeUpper === 'IMAGE';
    const isVideo = typeUpper === 'VIDEO';
    const displayContent = isImage
      ? 'Image Message'
      : (isVideo ? 'Video Message' : (m?.message || m?.text || m?.content || ''));
    const timestamp = m?.timestamp || m?.created_at || m?.sent_at || m?.updated_at || null;
    const isFromClient =
      m?.is_from_client === true ||
      String(m?.sender || m?.role || '').toLowerCase().includes('client');
    const isFromProvider =
      m?.is_from_provider === true ||
      String(m?.sender || m?.role || '').toLowerCase().includes('provider') ||
      String(m?.sender || m?.role || '').toLowerCase().includes('vet') ||
      String(m?.sender || m?.role || '').toLowerCase().includes('agent');
    const author = isFromClient ? 'Client' : (isFromProvider ? 'Provider' : (m?.sender || m?.role || 'System'));
    return {
      id: m?.id || m?._id || idx,
      author,
      isClient: isFromClient,
      content: displayContent,
      isMediaPlaceholder: isImage || isVideo,
      timestamp,
      messageType: typeUpper
    };
  });
};

const ParticipantDetailsScreen = () => {
  const { pet_incubation_id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [participant, setParticipant] = useState(null);
  const [incubationName, setIncubationName] = useState(null);
  const [incubations, setIncubations] = useState({});
  const [incubationSurveys, setIncubationSurveys] = useState([]);
  const [sendingSurveyIndex, setSendingSurveyIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeIncubationId, setMergeIncubationId] = useState('');
  const [mergeConfirm, setMergeConfirm] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  const [consultationsByPet, setConsultationsByPet] = useState({});
  const [consultationsLoading, setConsultationsLoading] = useState(false);
  const [selectedPetName, setSelectedPetName] = useState('');
  const [consultationsPage, setConsultationsPage] = useState(1);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);

  const CONSULTATIONS_PER_PAGE = 10;

  const fetchParticipant = async () => {
    if (!pet_incubation_id) {
      setError('No pet incubation ID provided');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getParticipantByPetIncubationId(pet_incubation_id);
      if (response?.data?.success) {
        setParticipant(response.data.participant || null);
        setIncubationName(response.data.incubation_name || null);
        setIncubations(response.data.incubations || {});
        setIncubationSurveys(
          Array.isArray(response.data.incubation_surveys) ? response.data.incubation_surveys : []
        );
      } else {
        setError(response?.data?.error || 'Failed to fetch participant');
      }
    } catch (err) {
      if (err?.message?.includes('Authentication required')) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(err?.message || 'An error occurred while fetching participant');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipant();
  }, [pet_incubation_id, navigate]);

  useEffect(() => {
    const fields = participant?.incubation_fields || {};
    const email = fields[displayStringToKey('Owner Email Address')];
    if (!email || typeof email !== 'string' || !email.trim()) {
      setConsultationsByPet({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setConsultationsLoading(true);
        const response = await submitExternalConsultation({
          email: email.trim(),
          partner_name: 'hills'
        });
        const map =
          response?.data?.care_consultations ||
          response?.data?.data?.care_consultations ||
          {};

        const grouped = {};
        Object.entries(map).forEach(([name, list]) => {
          if (!name || !Array.isArray(list)) return;
          const seen = new Set();
          const deduped = list.filter((item) => {
            const key = item?.id || item?._id || item?.e_id || JSON.stringify(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          grouped[name] = deduped;
        });

        if (!cancelled) setConsultationsByPet(grouped);
      } catch (err) {
        console.error('Failed to fetch consultations:', err);
        if (!cancelled) setConsultationsByPet({});
      } finally {
        if (!cancelled) setConsultationsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [participant]);

  // Build dropdown options and auto-select the current pet name when possible.
  useEffect(() => {
    const allNames = Object.keys(consultationsByPet).filter(
      (n) => n && n.toLowerCase() !== 'unassigned'
    );
    const currentName = participant?.incubation_fields?.[displayStringToKey('Pet First Name')] || '';
    const currentLower = currentName ? String(currentName).toLowerCase().trim() : '';

    if (!selectedPetName || !allNames.includes(selectedPetName)) {
      const match = allNames.find((n) => n.toLowerCase() === currentLower);
      if (match) {
        setSelectedPetName(match);
      } else if (allNames.length > 0) {
        setSelectedPetName(allNames[0]);
      } else {
        setSelectedPetName('');
      }
    }
  }, [consultationsByPet, participant]);

  // Reset pagination when selection changes.
  useEffect(() => {
    setConsultationsPage(1);
  }, [selectedPetName]);

  const petDropdownOptions = Object.keys(consultationsByPet)
    .filter((n) => n && n.toLowerCase() !== 'unassigned')
    .map((n) => ({ value: n, label: formatEngagementText(n) }));

  const activeConsultations = (consultationsByPet[selectedPetName] || [])
    .filter((c) => (c?.status || '').toUpperCase() !== 'CANCELED');

  const consultationTimestamp = (c) => {
    const raw = c?.updated_at || c?.created_at || c?.last_message_sent_at;
    const t = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  const sortedConsultations = [...activeConsultations].sort(
    (a, b) => consultationTimestamp(b) - consultationTimestamp(a)
  );

  const totalConsultationPages = Math.max(
    1,
    Math.ceil(sortedConsultations.length / CONSULTATIONS_PER_PAGE)
  );
  const currentPage = Math.min(consultationsPage, totalConsultationPages);
  const pageStart = (currentPage - 1) * CONSULTATIONS_PER_PAGE;
  const pageRows = sortedConsultations.slice(pageStart, pageStart + CONSULTATIONS_PER_PAGE);

  const handleBack = () => navigate(-1);

  const handleSendSurvey = async (survey, index) => {
    if (sendingSurveyIndex !== null) return;
    if (!participant?.pet_incubation_id) {
      showError('Missing participant context. Cannot send survey.');
      return;
    }
    if (!survey?.url || !String(survey.url).trim()) {
      showError('This survey is missing a URL.');
      return;
    }
    try {
      setSendingSurveyIndex(index);
      const response = await sendParticipantSurvey({
        pet_incubation_id: participant.pet_incubation_id,
        survey: { name: survey.name || 'Survey', url: survey.url }
      });
      if (response?.data?.success) {
        const email = response.data.email;
        showSuccess(email ? `Survey sent to ${email}.` : 'Survey sent.');
      } else {
        showError(response?.data?.error || 'Failed to send survey.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        showError('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      showError(err?.response?.data?.error || err?.message || 'Failed to send survey.');
    } finally {
      setSendingSurveyIndex(null);
    }
  };

  const handleConsultationRowClick = async (consultation) => {
    setSelectedConsultation(consultation || null);
    setIsConsultationModalOpen(true);
    setChatMessages([]);
    setChatError(null);
    setChatLoading(false);

    const typeUpper = String(consultation?.type || '').toUpperCase();
    if (typeUpper === 'CHAT' || typeUpper === 'THREAD') {
      try {
        setChatLoading(true);
        const careConsultationId =
          consultation?.id || consultation?._id || consultation?.e_id || consultation?.code;
        if (!careConsultationId) {
          setChatError('Missing consultation id for chat retrieval');
          setChatLoading(false);
          return;
        }
        const response = await getExternalChatMessages({ care_consultation_id: careConsultationId });
        setChatMessages(extractMessagesFromResponse(response?.data));
      } catch (err) {
        console.error('Failed to fetch chat messages:', err);
        setChatError('Failed to fetch chat messages');
      } finally {
        setChatLoading(false);
      }
    }
  };

  const closeConsultationModal = () => {
    setIsConsultationModalOpen(false);
    setSelectedConsultation(null);
    setChatMessages([]);
    setChatError(null);
    setChatLoading(false);
  };

  const openMergeModal = () => {
    setMergeIncubationId('');
    setMergeConfirm(false);
    setIsMergeModalOpen(true);
  };

  const closeMergeModal = () => {
    setIsMergeModalOpen(false);
    setMergeIncubationId('');
    setMergeConfirm(false);
  };

  const handleMergeClick = async () => {
    const trimmed = (mergeIncubationId || '').trim();
    if (!trimmed) {
      showError('Please enter a valid Pet Incubation ID.');
      return;
    }
    if (trimmed === participant?.pet_incubation_id) {
      showError('Cannot merge a participant into itself.');
      return;
    }
    if (!mergeConfirm) {
      setMergeConfirm(true);
      return;
    }
    if (!participant?.pet_id) {
      showError('Missing pet ID on current participant.');
      return;
    }
    try {
      if (isMerging) return;
      setIsMerging(true);
      const res = await mergeDuplicatePet({
        pet_incubation_id: trimmed,
        universal_pet_id: participant.pet_id
      });
      if (res?.data?.success) {
        showSuccess('Merge successful');
        await fetchParticipant();
        closeMergeModal();
      } else {
        showError(res?.data?.error || 'Merge failed');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        showError('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      showError(err?.response?.data?.error || err?.message || 'Unexpected error during merge');
      console.error('Merge error:', err);
    } finally {
      setIsMerging(false);
    }
  };

  const renderHeader = (title, subtitle) => (
    <div className="pd-topbar">
      <div className="pd-topbar-left">
        <button className="pd-back-btn" onClick={handleBack} aria-label="Back">
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="pd-topbar-title-group">
          <span className="pd-topbar-eyebrow">Participant</span>
          <div className="pd-topbar-title-row">
            <h1 className="pd-topbar-title">{title}</h1>
            {subtitle && <span className="pd-id-chip">{subtitle}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="participant-details-screen">
        {renderHeader('Participant Details')}
        <div className="pd-state">
          <Spinner full size="lg" label="Loading participant..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="participant-details-screen">
        {renderHeader('Participant Details')}
        <div className="pd-state pd-error">
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '16px' }}></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const fields = participant?.incubation_fields || {};
  const petName = readFields(fields, { keys: [displayStringToKey('Pet First Name')] }) || 'Participant';
  const displayPetIncubationId = participant?.pet_incubation_id
    ? String(participant.pet_incubation_id).toUpperCase()
    : '';

  return (
    <div className="participant-details-screen">
      {renderHeader(petName, displayPetIncubationId)}

      <div className="pd-content">
        <div className="pd-grid">
          <div className="pd-column">
            <section className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-heading">
                  <h2 className="pd-section-title">Pet Information</h2>
                  {incubationName && <p className="pd-section-sub">Scoped to: {incubationName}</p>}
                </div>
              </div>
              <div className="pd-section-body">
                {PET_FIELDS.map(spec => {
                  const value = readFields(fields, spec);
                  return (
                    <div key={spec.label} className="pd-row">
                      <span className="pd-label">{spec.label}</span>
                      <span className={`pd-value${value ? '' : ' empty'}`}>
                        {value || 'Not set'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-heading">
                  <h2 className="pd-section-title">Engagements</h2>
                  <p className="pd-section-sub">Consultations associated with the pet parent</p>
                </div>
                {petDropdownOptions.length > 0 && (
                  <Select
                    className="pd-pet-select"
                    options={petDropdownOptions}
                    value={selectedPetName}
                    onChange={(e) => setSelectedPetName(e.target.value)}
                    placeholder="Select Pet"
                  />
                )}
              </div>
              <div className="pd-section-body">
                {consultationsLoading ? (
                  <div className="pd-empty">
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
                    Loading consultations...
                  </div>
                ) : !selectedPetName || sortedConsultations.length === 0 ? (
                  <div className="pd-empty">No consultations found for this pet.</div>
                ) : (
                  <>
                    <table className="pd-engagements-table">
                      <thead>
                        <tr>
                          <th>Engagement</th>
                          <th>Type</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((c, index) => {
                          const engagement = formatEngagementText(c?.subject || c?.category || c?.status || 'N/A');
                          const type = formatConsultationType(c?.type || '');
                          const dateRaw = c?.updated_at || c?.created_at || c?.last_message_sent_at;
                          const date = dateRaw ? new Date(dateRaw).toLocaleDateString() : 'N/A';
                          const rowKey = c?.id || c?._id || c?.e_id || index;
                          return (
                            <tr key={rowKey} onClick={() => handleConsultationRowClick(c)}>
                              <td>{engagement}</td>
                              <td>{type}</td>
                              <td className="pd-engagements-date">{date}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {totalConsultationPages > 1 && (
                      <div className="pd-pagination">
                        <Button
                          variant="outline"
                          size="small"
                          className="pd-pagination-btn"
                          onClick={() => setConsultationsPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <i className="fas fa-chevron-left"></i>
                          Previous
                        </Button>
                        <span className="pd-pagination-status">
                          Page {currentPage} of {totalConsultationPages}
                        </span>
                        <Button
                          variant="outline"
                          size="small"
                          className="pd-pagination-btn"
                          onClick={() => setConsultationsPage((p) => Math.min(totalConsultationPages, p + 1))}
                          disabled={currentPage === totalConsultationPages}
                        >
                          Next
                          <i className="fas fa-chevron-right"></i>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="pd-column">
            <section className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-heading">
                  <h2 className="pd-section-title">Pet Parent</h2>
                </div>
              </div>
              <div className="pd-section-body">
                {PARENT_FIELDS.map(spec => {
                  const value = readFields(fields, spec);
                  return (
                    <div key={spec.label} className="pd-row">
                      <span className="pd-label">{spec.label}</span>
                      <span className={`pd-value${value ? '' : ' empty'}`}>
                        {value || 'Not set'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-heading">
                  <h2 className="pd-section-title">Incubations</h2>
                  <p className="pd-section-sub">Every incubation this pet belongs to</p>
                </div>
                <Button
                  variant="primary"
                  size="small"
                  className="pd-header-action-btn"
                  onClick={openMergeModal}
                >
                  <i className="fas fa-link"></i>
                  Merge Duplicate Pet
                </Button>
              </div>
              <div className="pd-section-body">
                {Object.keys(incubations).length === 0 ? (
                  <div className="pd-empty">No incubations found</div>
                ) : (
                  <ul className="pd-incubations-list">
                    {Object.entries(incubations).map(([key, name]) => {
                      const isCurrent = key === participant?.pet_incubation_id;
                      return (
                        <li
                          key={key}
                          className={`pd-incubation-item${isCurrent ? ' is-current' : ''}`}
                        >
                          <div className="pd-incubation-name">
                            {name}
                            {isCurrent && <span className="pd-current-pill">Current</span>}
                          </div>
                          <div className="pd-incubation-key">{String(key).toUpperCase()}</div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

            <section className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-heading">
                  <h2 className="pd-section-title">Surveys</h2>
                  <p className="pd-section-sub">Surveys linked to {incubationName || 'this incubation'}</p>
                </div>
              </div>
              <div className="pd-section-body">
                {incubationSurveys.length === 0 ? (
                  <div className="pd-empty">No surveys configured for this incubation</div>
                ) : (
                  <ul className="pd-surveys-list">
                    {incubationSurveys.map((survey, index) => {
                      const displayName = survey?.name && survey.name.toLowerCase().includes('survey')
                        ? survey.name
                        : `${survey?.name || 'Untitled'} Survey`;
                      const isSending = sendingSurveyIndex === index;
                      const disabled = !survey?.url || !String(survey.url).trim() || sendingSurveyIndex !== null;
                      return (
                        <li key={`${index}-${survey?.name || ''}`} className="pd-survey-item">
                          <div className="pd-survey-info">
                            <div className="pd-survey-name">{displayName}</div>
                            {survey?.url && (
                              <a
                                href={survey.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pd-survey-url"
                              >
                                {survey.url}
                              </a>
                            )}
                          </div>
                          <Button
                            variant="primary"
                            size="small"
                            className="pd-header-action-btn"
                            onClick={() => handleSendSurvey(survey, index)}
                            disabled={disabled}
                            loading={isSending}
                          >
                            <i className="fas fa-paper-plane"></i>
                            {isSending ? 'Sending...' : 'Send'}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {isConsultationModalOpen && (
        <div
          className="pd-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeConsultationModal}
        >
          <div className="pd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <h3 className="pd-modal-title">Consultation Details</h3>
              <button
                className="pd-modal-close"
                onClick={closeConsultationModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="pd-modal-body">
              <div className="pd-modal-row">
                <span className="pd-modal-row-label">Pet Name</span>
                <span className="pd-modal-row-value">
                  {formatEngagementText(selectedConsultation?.patient?.name || 'N/A')}
                </span>
              </div>
              <div className="pd-modal-row">
                <span className="pd-modal-row-label">Breed</span>
                <span className="pd-modal-row-value">
                  {selectedConsultation?.patient?.breed || 'N/A'}
                </span>
              </div>
              <div className="pd-modal-row">
                <span className="pd-modal-row-label">Category</span>
                <span className="pd-modal-row-value">
                  {formatEngagementText(selectedConsultation?.category || 'N/A')}
                </span>
              </div>
              <div className="pd-modal-row">
                <span className="pd-modal-row-label">Resolved Date</span>
                <span className="pd-modal-row-value">
                  {selectedConsultation?.resolved_at
                    ? new Date(selectedConsultation.resolved_at).toLocaleString()
                    : 'N/A'}
                </span>
              </div>

              {(() => {
                const typeUpper = String(selectedConsultation?.type || '').toUpperCase();
                if (typeUpper !== 'CHAT' && typeUpper !== 'THREAD') return null;
                return (
                  <div className="pd-chat-section">
                    <h4 className="pd-chat-title">Chat Transcript</h4>
                    {chatLoading && (
                      <div className="pd-chat-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading chat messages...</span>
                      </div>
                    )}
                    {chatError && (
                      <div className="pd-chat-error">
                        <i className="fas fa-exclamation-triangle"></i>
                        {chatError}
                      </div>
                    )}
                    {!chatLoading && !chatError && (
                      chatMessages.length > 0 ? (
                        <div className="pd-chat-messages">
                          {chatMessages.map((m) => (
                            <div
                              key={m.id}
                              className={`pd-chat-message ${m.isClient ? 'from-client' : 'from-provider'}`}
                            >
                              <div className="pd-chat-message-meta">
                                <span className="pd-chat-author">{m.author}</span>
                                {m.timestamp && (
                                  <span className="pd-chat-timestamp">
                                    {new Date(m.timestamp).toLocaleString()}
                                  </span>
                                )}
                              </div>
                              <div className="pd-chat-content">
                                {m.isMediaPlaceholder ? <em>{m.content}</em> : (m.content || ' ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="pd-chat-empty">No chat messages found.</div>
                      )
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {isMergeModalOpen && (
        <div
          className="pd-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeMergeModal}
        >
          <div className="pd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <h3 className="pd-modal-title">Merge Duplicate Pet</h3>
              <button
                className="pd-modal-close"
                onClick={closeMergeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="pd-modal-body">
              <label htmlFor="pd-merge-id-input" className="pd-modal-field-label">
                Enter Pet Incubation ID to merge into this pet
              </label>
              <Input
                id="pd-merge-id-input"
                placeholder="Enter Pet Incubation ID"
                value={mergeIncubationId}
                onChange={(e) => setMergeIncubationId(e.target.value)}
              />
            </div>
            {mergeConfirm && (
              <div className="pd-modal-warning">This action cannot be reversed.</div>
            )}
            <div className="pd-modal-footer">
              <Button
                variant="outline"
                size="medium"
                onClick={closeMergeModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleMergeClick}
                loading={isMerging}
                disabled={isMerging}
                ripple
              >
                {mergeConfirm ? 'Confirm Merge' : 'Merge'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantDetailsScreen;
